import { useMemo } from 'react';
import { useAppSelector } from '../redux/hooks';
import {
  useGetComponentListQuery,
  ComponentListItem,
} from '../redux/api/entityApi';
import type { Entity } from '../types/api';

/**
 * Hook for fetching component list for the component specifications view.
 *
 * Uses a single-request endpoint that returns all components with only the
 * fields needed for the component list (uuid, primary_id, component_type, properties).
 *
 * Benefits over the paginated entities endpoint:
 * - Single request instead of 10+ paginated requests
 * - Smaller payload (only needed fields)
 * - 5-minute caching
 */
export const useComponentList = (exclude_modules: boolean = true) => {
  const currentSite = useAppSelector((state) => state.site.currentSite);

  const portfolioId = currentSite?.portfolio_id;
  const siteId = currentSite?.site_id;

  const {
    data: componentData,
    isFetching,
    error,
  } = useGetComponentListQuery(
    {
      portfolio_id: portfolioId!,
      site_id: siteId!,
      exclude_modules,
    },
    {
      skip: !portfolioId || !siteId,
    }
  );

  // Map ComponentListItem to Entity-like format for compatibility with existing UI
  const entities = useMemo(() => {
    if (!componentData?.items) return [];

    return componentData.items.map((item: ComponentListItem): Entity => ({
      // Core fields
      id: item.uuid,
      name: item.primary_id,
      concept_id: item.component_type || '',
      site_id: siteId || '',
      tenant_id: '',

      // Legacy field mappings for compatibility
      entity_id: item.uuid,
      entity_name: item.primary_id,
      entity_type_id: item.component_type || '',
      entity_type_name: item.component_type || '',
      concept_name: item.component_type || '',
      concept_category: item.component_type || '',
      pvcollada_component_type: item.component_type || '',

      // Properties/metadata
      properties: (item.properties || {}) as Record<string, unknown>,
      metadata: (item.properties || {}) as Record<string, unknown>,
      entity_metadata: (item.properties || {}) as Record<string, unknown>,

      // Empty/default values for unused fields
      parent_entity_id: null,
      description: null,
      manufacturer: null,
      model: null,
      serial_number: null,
      installation_date: null,
      warranty_expiry: null,
      status: 'active',
      latitude: null,
      longitude: null,
      altitude: null,
      orientation: null,
      tilt: null,
      version: 1,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      component_order: undefined,
      aliases: [],
      configuration: {},
      pref: undefined,
    }));
  }, [componentData?.items, siteId]);

  return {
    entities,
    totalCount: componentData?.total_count || 0,
    isLoading: isFetching,
    error,
  };
};
